import React, { useEffect, useRef, useState } from 'react';
import { useDialog } from '@context/DialogContext';
import api from '@api/axios';
import { useLoading } from '@context/LoadingContext';
import PlannedProjectSearchV2 from '@components/common/Table/PlannedProjectSearchV2';
import PlannedProjectListTableV2 from '@components/common/Table/PlannedProjectListTableV2';
import Fab from "@mui/material/Fab";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const PlannedProjectList = () => {
  const { showLoading, hideLoading } = useLoading();
  const { showDialog } = useDialog();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(30);
  const [projectList, setProjectList] = useState([]);
  const [projectListTotal, setProjectListTotal] = useState(0);
  const [checkedProjectList, setCheckedProjectList] = useState([]);
  
  // 검색 : 정적 파라미터
  const [search, setSearch] = useState({
    projectName: '',
    orderingCompanyName: '',
    accountCompanyName: '',
    projectManagerName: '',
    isProjectConfirm: false,
    orderBy: 'start_date',
    orderDir: 'DESC',
  });

  const onSearch = (page = 1, manualSearch = false, init = false) => {
    // // // // //
    /** setSearch 정적 파라미터 설정 */

    // 직접 검색 일때 동적 파라미터를 정적 파라미터에 저장. 동적 파라미터 바탕 초기화.
    if (manualSearch) setSearch(getSearch());

    // 초기화 검색 일때 정적 파라미터 초기화
    if (init) setSearch({
      projectName: '',
      orderingCompanyName: '',
      accountCompanyName: '',
      projectManagerName: '',
      orderBy: 'start_date',
      orderDir: 'DESC',
      isProjectConfirm: false,
    });

    // // // // //
    /** params 검색 파라미터 설정 */

    // 검색 파라미터 설정
    let searchParam = {}

    // 직접 검색 일때
    if (manualSearch) {
      searchParam = getSearch();
    }
    // 이외 검색 일때
    else {
      searchParam = { ...search };
    }

    // 검색일 때 페이지 초기화 (검색 버튼 클릭 시)
    if (manualSearch) setPage(1);

    // 페이지 번호 설정 (페이지 번호 클릭 시)
    if (page) setPage(page);

    // 검색 파라미터
    let params = {}

    // 초기화 검색
    if (init) {
      params = {
        page: 1,
        limit: limit,
        projectName: '',
        orderingCompany: '',
        accountCompany: '',
        managerName: '',
        orderBy: 'start_date',
        orderDir: 'DESC',
        isProjectConfirm: false,
      }
    }
    // 이외 검색
    else {
      params = {
        page: manualSearch ? 1 : page,
        limit: limit,
        projectName: searchParam?.projectName || '',
        orderingCompany: searchParam?.orderingCompanyName || '',
        accountCompany: searchParam?.accountCompanyName || '',
        managerName: searchParam?.projectManagerName || '',
        orderBy: searchParam?.orderBy || 'start_date',
        orderDir: searchParam?.orderDir || 'DESC',
        isProjectConfirm: searchParam?.isProjectConfirm || false,
      }
    }

    // // // // //
    /** API 호출 */

    showLoading();

    api
      .get('/api/project/readProjectList', {
        params: params,
      })
      .then((res) => {
        if (res.status === 200) {
          setProjectList(res.data.rows);
          setProjectListTotal(res.data.totalCount);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const onDeleteProject = () => {
    if (checkedProjectList.length === 0) {
      showDialog({
        type: 'alert',
        title: '확인',
        message: '삭제할 프로젝트를 선택해 주세요.',
      });
    }
    else {
      showDialog({
        type: 'confirm',
        title: '삭제',
        message: '정말 삭제하시겠습니까?',
        onConfirm: () => {
          showLoading();
          api.delete('/api/project/deleteProject', {
            params: {
              ids: checkedProjectList.join(','),
              isProjectConfirm: false,
            },
          })
            .then((res) => {
              console.log(res);
              if (res.status === 201) {
                showDialog({
                  type: 'alert',
                  title: '확인',
                  message: '삭제되었습니다.',
                });
                onSearch();
              }
            })
            .catch((err) => {
              console.log(err);
            })
            .finally(() => {
              hideLoading();
            });
        },
      });
    }
  };

  useEffect(() => {
    onSearch();
  }, [limit, search.orderBy, search.orderDir]);

  const searchRef = useRef();

  const getSearch = () => {
    return searchRef?.current?.getSearch();
  };

  const scrollToTop = () => {
    const scrollableElement = document.querySelector('.Toolpad_DashboardLayout-main') || 
                             document.querySelector('main') || 
                             document.querySelector('.MuiBox-root[role="main"]');
    if (scrollableElement) {
      scrollableElement.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const scrollToBottom = () => {
    const scrollableElement = document.querySelector('.Toolpad_DashboardLayout-main') || 
                             document.querySelector('main') || 
                             document.querySelector('.MuiBox-root[role="main"]');
    if (scrollableElement) {
      scrollableElement.scrollTo({
        top: scrollableElement.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  return (
    <React.Fragment>
      <PlannedProjectSearchV2
        ref={searchRef}
        onSearch={onSearch}
      />

      <PlannedProjectListTableV2
        projectList={projectList}
        projectListTotal={projectListTotal}
        staticSearch={search}
        setStaticSearch={setSearch}
        page={page}
        limit={limit}
        setLimit={setLimit}
        onSearch={onSearch}
        onDeleteProject={onDeleteProject}
        checkedProjectList={checkedProjectList}
        setCheckedProjectList={setCheckedProjectList}
      />
      <div
        style={{
          position: "fixed",
          bottom: 16,
          right: 16,
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          zIndex: 1000,
        }}
      >
        <Fab color="primary" onClick={scrollToTop} size="medium">
          <KeyboardArrowUpIcon />
        </Fab>
        <Fab color="primary" onClick={scrollToBottom} size="medium">
          <KeyboardArrowDownIcon />
        </Fab>
      </div>
    </React.Fragment>
  );
};

export default PlannedProjectList;
